import React, {Component} from 'react';
import {injectIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import UpdateViewerMutation from '../../mutations/update_viewer_mutation.modern';
import * as tracking from '../../tracking';
import Util from '../../forecast-app/shared/util/util';
import Switch from '../../forecast-app/shared/components/switch/switch';
import RadioButton from '../../components/new-ui/radio_button';
import InputField from '../../components/new-ui/input_field';
import CustomScrollDiv from '../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import SetIntegrationNotificationSettingMutation from '../../mutations/SetIntegrationNotificationSettingMutation';
import {hasPermission} from '../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';

const NOTIFICATION_FREQUENCY = {
	INSTANT: 0,
	DAILY: 1,
	WEEKLY: 2,
};

class ProfileNotificationsSettings extends Component {
	constructor(props) {
		super(props);

		this.inputs = {};
		this.state = {
			isAdvancedVisible: false,
			projectDeadlineNotificationPeriod: this.props.viewer.projectDeadlineNotificationPeriod,
			taskDeadlineNotificationPeriod: this.props.viewer.taskDeadlineNotificationPeriod,
		};
	}

	componentDidMount() {
		document.title = 'Notifications - My profile - Forecast';

		tracking.trackPage('profile-notifications');
	}

	handleToggle(notification) {
		const params = {};
		params[notification] = !this.props.viewer[notification];
		Util.CommitMutation(UpdateViewerMutation, params);
	}

	handleTeamsToggle(teamNotification) {
		Util.CommitMutation(
			SetIntegrationNotificationSettingMutation,
			{
				teamSetting: `msteams_${teamNotification}`,
				personId: this.props.viewer.actualPersonId,
				value: !this.props.viewer.teamsNotificationSettings[teamNotification],
			},
			() => this.props.retry()
		);
	}

	handleAdvancedToggle(notification, inApp) {
		const notifyOn = inApp ? 'notifyInAppOnTask' : 'notifyOnTask';
		const params = {};
		const notifToChange = notifyOn + notification + 'Change';
		params[notifToChange] = !this.props.viewer[notifToChange];
		Util.CommitMutation(UpdateViewerMutation, params);
	}

	toggleAdvancedSettings() {
		this.setState({isAdvancedVisible: !this.state.isAdvancedVisible});
	}

	handleNotificationPeriodChange(type, value) {
		this.setState({[type + 'DeadlineNotificationPeriod']: value});
	}

	handleNotificationPeriodBlur(type) {
		//Clamp value between 0 and 99 because lower than 0 makes no sense and 99+ looks ugly
		let inputValue = parseFloat(this.state[type + 'DeadlineNotificationPeriod']);
		if (isNaN(inputValue)) {
			inputValue = 0;
		} else if (inputValue < 0) {
			inputValue = 0;
		} else if (inputValue > 99) {
			inputValue = 99;
		} else {
			inputValue = Math.floor(inputValue);
		}
		this.setState({[type + 'DeadlineNotificationPeriod']: inputValue});
		const mutationParams = {
			[type + 'DeadlineNotificationPeriod']: inputValue,
		};
		Util.CommitMutation(UpdateViewerMutation, mutationParams);
	}

	handleNotificationFrequencyChange(input) {
		this.setState({notificationFrequency: input.value});
		const mutationParams = {
			notificationFrequency: input.value,
		};
		Util.CommitMutation(UpdateViewerMutation, mutationParams);
	}

	getTopHeader() {
		const {formatMessage} = this.props.intl;
		return (
			<TopHeaderBarWrapper>
				<TopHeaderBar title={formatMessage({id: 'header.notifications'})} />
			</TopHeaderBarWrapper>
		);
	}

	render() {
		const {formatMessage} = this.props.intl;
		const viewer = this.props.viewer;
		const notificationFrequencyOptions = [
			{value: NOTIFICATION_FREQUENCY.INSTANT, label: formatMessage({id: 'profile.notifications.frequency_instant'})},
			{value: NOTIFICATION_FREQUENCY.DAILY, label: formatMessage({id: 'profile.notifications.frequency_daily'})},
			{value: NOTIFICATION_FREQUENCY.WEEKLY, label: formatMessage({id: 'profile.notifications.frequency_weekly'})},
		];

		const taskPart1 = formatMessage({id: 'profile.notifications.notify_on_task_deadline_part_1'});
		const projectPart1 = formatMessage({id: 'profile.notifications.notify_on_project_deadline_part_1'});

		let firstSectionData = [
			{type: 'notifyOnProjectStatusChange', typeInApp: 'notifyInAppOnProjectStatusChange', description: formatMessage({id: 'profile.notifications.notify_on_project_status_change'})},
			{type: 'notifyOnAssignedProject', typeInApp: 'notifyInAppOnAssignedProject', description: formatMessage({id: 'profile.notifications.notify_on_assigned_to_project'})},

			{
				type: 'notifyOnProjectDeadline',
				typeInApp: 'notifyInAppOnProjectDeadline',
				description: (
					<div className={'input-section'}>
						{projectPart1.length > 1 ? <div className={'first-part'}>{projectPart1}</div> : null}
						<InputField
							id={'project'}
							hideLabel={true}
							ref={input => (this.inputs['project'] = input)}
							type="number"
							value={this.state.projectDeadlineNotificationPeriod}
							disabled={viewer.disableNotifications || !viewer.notifyOnProjectDeadline}
							onChange={this.handleNotificationPeriodChange.bind(this, 'project')}
							onBlur={this.handleNotificationPeriodBlur.bind(this, 'project')}
						/>
						<div className={'second-part'}>{formatMessage({id: 'profile.notifications.notify_on_project_deadline_part_2'})}</div>
					</div>
				),
			},
			{
				type: 'notifyOnTaskDeadline',
				typeInApp: 'notifyInAppOnTaskDeadline',
				description: (
					<div className={'input-section'}>
						{taskPart1.length > 1 ? <div className={'first-part'}>{taskPart1}</div> : null}
						<InputField
							id={'task'}
							hideLabel={true}
							ref={input => (this.inputs['task'] = input)}
							type="number"
							value={this.state.taskDeadlineNotificationPeriod}
							disabled={viewer.disableNotifications || !viewer.notifyOnProjectDeadline}
							onChange={this.handleNotificationPeriodChange.bind(this, 'task')}
							onBlur={this.handleNotificationPeriodBlur.bind(this, 'task')}
						/>
						<div className={'second-part'}>{formatMessage({id: 'profile.notifications.notify_on_task_deadline_part_2'})}</div>
					</div>
				),
			},
			{type: 'notifyOnMention', typeInApp: 'notifyInAppOnMention', description: formatMessage({id: 'profile.notifications.notify_on_mention'})},
			{type: 'notifyOnAssignedTask', typeInApp: 'notifyInAppOnAssignedTask', description: formatMessage({id: 'profile.notifications.notify_on_assigned_to_task'})},
		];

		if (hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS)) {
			firstSectionData.unshift({type: 'notifyOnPersonJoin', adminOnly: true, typeInApp: 'notifyInAppOnPersonJoin', description: formatMessage({id: 'profile.notifications.notify_on_person_join_company'})});
		}

		const teamsEnabled = this.props.viewer.availableFeatureFlags.find(flag => flag.key === 'use_teams') && this.props.viewer.company.teamsEnabled;

		const advancedColumnData = [
			{type: 'Description', description: formatMessage({id: 'profile.notifications.notify_on_task_description_change'})},
			{type: 'Title', description: formatMessage({id: 'profile.notifications.notify_on_task_title_change'})},
			{type: 'Estimate', description: formatMessage({id: 'profile.notifications.notify_on_task_estimate_change'})},
			{type: 'StartDate', description: formatMessage({id: 'profile.notifications.notify_on_task_start_date_change'})},
			{type: 'EndDate', description: formatMessage({id: 'profile.notifications.notify_on_task_end_date_change'})},
			{type: 'Sprint', description: formatMessage({id: 'profile.notifications.notify_on_task_sprint_change'})},
			{type: 'Phase', description: formatMessage({id: 'profile.notifications.notify_on_task_phase_change'})},
			{type: 'StatusColumn', description: formatMessage({id: 'profile.notifications.notify_on_task_status_column_change'})},
			{type: 'Project', description: formatMessage({id: 'profile.notifications.notify_on_task_project_change'})},
			{type: 'Subtask', description: formatMessage({id: 'profile.notifications.notify_on_task_subtask_change'})},
			{type: 'SubtaskEstimate', description: formatMessage({id: 'profile.notifications.notify_on_task_subtask_estimate_change'})},
			{type: 'Comment', description: formatMessage({id: 'profile.notifications.notify_on_task_comment_change'})},
			{type: 'File', description: formatMessage({id: 'profile.notifications.notify_on_task_file_change'})},
			{type: 'Bug', description: formatMessage({id: 'profile.notifications.notify_on_task_bug_change'})},
			{type: 'Blocked', description: formatMessage({id: 'profile.notifications.notify_on_task_blocked_change'})},
			{type: 'Repeating', description: formatMessage({id: 'profile.notifications.notify_on_task_repeating_change'})},
		];

		return (
			<div className="section-content settings-app ">
				{this.getTopHeader()}
				<CustomScrollDiv>
					<div className="settings-notifications v2" data-cy={'notification-settings-page'}>
						{this.props.children}
						<div className="master-section">
							<div className="section-header">
								<div className="column-description">{formatMessage({id: 'profile.notifications.notification_types'})}</div>
							</div>
							<div className="slider-container">
								<div className="description">{formatMessage({id: 'profile.notifications.enable_app_notifications'})}</div>
								<Switch onChange={this.handleToggle.bind(this, 'disableInAppNotifications')} checked={!viewer.disableInAppNotifications} sliderWidth={38} sliderHeight={20} />
							</div>
							<div className="slider-container">
								<div className="description">{formatMessage({id: 'profile.notifications.enable_email_notifications'})}</div>
								<Switch onChange={this.handleToggle.bind(this, 'disableNotifications')} checked={!viewer.disableNotifications} sliderWidth={38} sliderHeight={20} />
							</div>
							{teamsEnabled ? (
								<div className="slider-container">
									<div className="description">{formatMessage({id: 'profile.notifications.enable_teams_notifications'})}</div>
									<Switch onChange={this.handleTeamsToggle.bind(this, 'disableTeamsNotifications')} checked={!viewer.teamsNotificationSettings.disableTeamsNotifications} sliderWidth={38} sliderHeight={20} />
								</div>
							) : (
								''
							)}
						</div>
						<div className="frequency-section">
							<div className="section-header">
								<div className="column-description">{formatMessage({id: 'profile.notifications.notification_frequency'})}</div>
								<div className="column-app" />
								<div className="column-email">{formatMessage({id: 'common.email'})}</div>
							</div>
							{notificationFrequencyOptions.map((item, index) => {
								return (
									<div key={'first-section-item-' + index} className="slider-container">
										<div className="column-description">{item.label}</div>
										<div className="column-app" />
										<div className="column-email">
											<RadioButton onSelected={this.handleNotificationFrequencyChange.bind(this, item)} stateValue={this.props.viewer.notificationFrequency} value={item.value} />
										</div>
									</div>
								);
							})}
						</div>
						<div className="second-section">
							<div className="section-header">
								<div className="column-description">{formatMessage({id: 'profile.notifications.notify_me'})}</div>
								<div className="column-app">{formatMessage({id: 'common.app'})}</div>
								<div className="column-email">{formatMessage({id: 'common.email'})}</div>
								{teamsEnabled ? <div className="column-email">{formatMessage({id: 'common.microsoft_teams'})}</div> : ''}
							</div>
							{firstSectionData.map((item, index) => {
								return (
									<div key={'first-section-item-' + index} className="slider-container">
										<div className="column-description">{item.description}</div>
										<div className="column-app">
											<Switch
												locked={viewer.disableInAppNotifications}
												onChange={this.handleToggle.bind(this, item.typeInApp)}
												checked={viewer[item.typeInApp] && !viewer.disableInAppNotifications}
												sliderWidth={38}
												sliderHeight={20}
											/>
										</div>
										<div className="column-email">
											<Switch locked={viewer.disableNotifications} onChange={this.handleToggle.bind(this, item.type)} checked={viewer[item.type] && !viewer.disableNotifications} sliderWidth={38} sliderHeight={20} />
										</div>
										{teamsEnabled ? (
											<div className="column-email">
												<Switch
													locked={viewer.teamsNotificationSettings.disableTeamsNotifications}
													onChange={this.handleTeamsToggle.bind(this, item.type)}
													checked={viewer.teamsNotificationSettings[item.type] && !viewer.teamsNotificationSettings.disableTeamsNotifications}
													sliderWidth={38}
													sliderHeight={20}
												/>
											</div>
										) : (
											''
										)}
									</div>
								);
							})}
						</div>
						<div className="advanced-section">
							<div className="first-advanced-section">
								<div className={'slider-section'}>
									<div className="column-description">{formatMessage({id: 'profile.notifications.notify_on_task_update'})}</div>
									<div className="column-app">
										<Switch
											locked={viewer.disableInAppNotifications}
											onChange={this.handleToggle.bind(this, 'notifyInAppOnAssignedTaskUpdate')}
											checked={viewer.notifyInAppOnAssignedTaskUpdate && !viewer.disableInAppNotifications}
											sliderWidth={38}
											sliderHeight={20}
										/>
									</div>
									<div className="column-email">
										<Switch
											locked={viewer.disableNotifications}
											onChange={this.handleToggle.bind(this, 'notifyOnAssignedTaskUpdate')}
											checked={viewer.notifyOnAssignedTaskUpdate && !viewer.disableNotifications}
											sliderWidth={38}
											sliderHeight={20}
										/>
									</div>
									{teamsEnabled ? (
										<div className="column-email">
											<Switch
												locked={viewer.teamsNotificationSettings.disableTeamsNotifications}
												onChange={this.handleTeamsToggle.bind(this, 'notifyOnAssignedTaskUpdate')}
												checked={viewer.teamsNotificationSettings.notifyOnAssignedTaskUpdate && !viewer.teamsNotificationSettings.disableTeamsNotifications}
												sliderWidth={38}
												sliderHeight={20}
											/>
										</div>
									) : (
										''
									)}
								</div>
							</div>

							<button className={'show-advanced-settings-button ' + (this.state.isAdvancedVisible ? 'expanded' : 'collapsed')} onClick={this.toggleAdvancedSettings.bind(this)}>
								{this.state.isAdvancedVisible ? formatMessage({id: 'profile.notifications.hide_advanced_settings'}) : formatMessage({id: 'profile.notifications.show_advanced_settings'})}
							</button>

							{this.state.isAdvancedVisible ? (
								<div className="second-advanced-section">
									<div className="advanced-settings-description">{formatMessage({id: 'profile.notifications.selected_task_notification_settings'})}</div>
									{advancedColumnData.map((item, index) => (
										<div key={'first-column-item-' + index} className="slider-container">
											<div className="column-description">{item.description}</div>
											<div className="column-app">
												<Switch
													locked={viewer.disableInAppNotifications}
													onChange={this.handleAdvancedToggle.bind(this, item.type, true)}
													checked={viewer['notifyInAppOnTask' + item.type + 'Change'] && !viewer.disableInAppNotifications}
													sliderWidth={38}
													sliderHeight={20}
												/>
											</div>
											<div className="column-email">
												<Switch
													locked={viewer.disableNotifications}
													onChange={this.handleAdvancedToggle.bind(this, item.type, false)}
													checked={viewer['notifyOnTask' + item.type + 'Change'] && !viewer.disableNotifications}
													sliderWidth={38}
													sliderHeight={20}
												/>
											</div>
											{teamsEnabled ? (
												<div className="column-email">
													<Switch
														locked={viewer.teamsNotificationSettings.disableTeamsNotifications}
														onChange={this.handleTeamsToggle.bind(this, 'notifyOnTask' + item.type + 'Change', false)}
														checked={viewer.teamsNotificationSettings['notifyOnTask' + item.type + 'Change'] && !viewer.teamsNotificationSettings.disableNotifications}
														sliderWidth={38}
														sliderHeight={20}
													/>
												</div>
											) : (
												''
											)}
										</div>
									))}
								</div>
							) : (
								<div className="second-advanced-section" />
							)}
						</div>
					</div>
				</CustomScrollDiv>
			</div>
		);
	}
}

const profileNotificationsSettingsQuery = graphql`
	query profileNotificationsSettings_Query {
		viewer {
			actualPersonId
			component(name: "settings_notifications")
			...profileNotificationsSettings_viewer
		}
	}
`;

export {profileNotificationsSettingsQuery};

export default createFragmentContainer(injectIntl(ProfileNotificationsSettings), {
	viewer: graphql`
		fragment profileNotificationsSettings_viewer on Viewer {
			id
			availableFeatureFlags {
				key
			}
			company {
				teamsEnabled
			}
			actualPersonId
			notificationFrequency
			notifyOnProjectStatusChange
			notifyOnAssignedProject
			notifyOnPersonJoin
			notifyOnProjectDeadline
			notifyOnAssignedTask
			notifyOnAssignedTaskUpdate
			notifyOnTaskDeadline
			notifyOnMention
			disableNotifications
			projectDeadlineNotificationPeriod
			taskDeadlineNotificationPeriod
			notifyOnTaskDescriptionChange
			notifyOnTaskTitleChange
			notifyOnTaskEstimateChange
			notifyOnTaskStartDateChange
			notifyOnTaskEndDateChange
			notifyOnTaskSprintChange
			notifyOnTaskPhaseChange
			notifyOnTaskStatusColumnChange
			notifyOnTaskProjectChange
			notifyOnTaskSubtaskChange
			notifyOnTaskSubtaskEstimateChange
			notifyOnTaskCommentChange
			notifyOnTaskFileChange
			notifyOnTaskBugChange
			notifyOnTaskBlockedChange
			notifyOnTaskRepeatingChange
			notifyInAppOnProjectStatusChange
			notifyInAppOnAssignedProject
			notifyInAppOnPersonJoin
			notifyInAppOnProjectDeadline
			notifyInAppOnAssignedTask
			notifyInAppOnAssignedTaskUpdate
			notifyInAppOnTaskDeadline
			notifyInAppOnMention
			disableInAppNotifications
			notifyInAppOnTaskDescriptionChange
			notifyInAppOnTaskTitleChange
			notifyInAppOnTaskEstimateChange
			notifyInAppOnTaskStartDateChange
			notifyInAppOnTaskEndDateChange
			notifyInAppOnTaskSprintChange
			notifyInAppOnTaskPhaseChange
			notifyInAppOnTaskStatusColumnChange
			notifyInAppOnTaskProjectChange
			notifyInAppOnTaskSubtaskChange
			notifyInAppOnTaskSubtaskEstimateChange
			notifyInAppOnTaskCommentChange
			notifyInAppOnTaskFileChange
			notifyInAppOnTaskBugChange
			notifyInAppOnTaskBlockedChange
			notifyInAppOnTaskRepeatingChange
			teamsNotificationSettings {
				disableNotifications
				notifyOnProjectStatusChange
				notifyOnAssignedProject
				notifyOnPersonJoin
				notifyOnProjectDeadline
				notifyOnAssignedTask
				notifyOnAssignedTaskUpdate
				notifyOnTaskDeadline
				notifyOnMention
				notifyOnTaskDescriptionChange
				notifyOnTaskTitleChange
				notifyOnTaskEstimateChange
				notifyOnTaskStartDateChange
				notifyOnTaskEndDateChange
				notifyOnTaskSprintChange
				notifyOnTaskPhaseChange
				notifyOnTaskStatusColumnChange
				notifyOnTaskProjectChange
				notifyOnTaskSubtaskChange
				notifyOnTaskSubtaskEstimateChange
				notifyOnTaskCommentChange
				notifyOnTaskFileChange
				notifyOnTaskBugChange
				notifyOnTaskBlockedChange
				notifyOnTaskRepeatingChange
			}
		}
	`,
});
